import { useSyncExternalStore } from 'react';
import { finishUpload, startUpload, type SendTarget } from '../api/lesson-videos.js';
import { MAX_SECONDS } from './video-limits.js';
import { probeVideo } from './video-probe.js';
import { sendInBlocks, sendWhole } from './block-upload.js';

/**
 * The videos being sent, for the whole app rather than one screen (ruled 16
 * October: "independent and asynchronous of the UI"). A send started on a
 * lesson carries on while the athlete goes Home or to Tools; the strip above
 * the tabs shows it, and the lesson page shows it as a row.
 *
 * Held in memory, deliberately: a send is a few minutes' work on the phone,
 * and what it sends is the video still in the phone's library. Nothing here
 * is a record of anything (RE-33 applies to the video once it has arrived,
 * which is in storage and the database, not here).
 */

type GetToken = () => Promise<string | null>;

export type SendState = 'reading' | 'sending' | 'too-long' | 'failed' | 'done';

export interface VideoSend {
  key: string;
  lessonId: string;
  /** The lesson page the strip leads back to. */
  href: string;
  state: SendState;
  durationSeconds: number;
  thumbnailUrl: string | null;
  sentBytes: number;
  totalBytes: number;
  paused: boolean;
  /** Set once the API has recorded it, while the lesson's list catches up. */
  videoId?: string;
}

let sends: VideoSend[] = [];
const listeners = new Set<() => void>();
const stoppers = new Map<string, AbortController>();
/**
 * The phone's own still for a video it sent, by video id, for the rest of the
 * session: the row that replaces the sending one shows this at once instead of
 * a blank square while the stored thumbnail loads (16 October).
 */
const localThumbnails = new Map<string, string>();

export function localThumbnail(videoId: string): string | undefined {
  return localThumbnails.get(videoId);
}

/** Lessons whose list should be read again because a send finished there. */
const finished = new Set<() => void>();

function publish(next: VideoSend[]) {
  sends = next;
  listeners.forEach((listener) => listener());
}

function update(key: string, change: Partial<VideoSend>) {
  publish(sends.map((send) => (send.key === key ? { ...send, ...change } : send)));
}

function drop(key: string) {
  const send = sends.find((candidate) => candidate.key === key);
  if (send?.thumbnailUrl && !send.videoId) URL.revokeObjectURL(send.thumbnailUrl);
  stoppers.delete(key);
  publish(sends.filter((candidate) => candidate.key !== key));
}

export function sendVideo(getToken: GetToken, lessonId: string, file: File, target: SendTarget): void {
  const key = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const stopper = new AbortController();
  stoppers.set(key, stopper);
  // Choosing again clears the last too-long message for this lesson.
  publish([
    ...sends.filter((send) => !(send.lessonId === lessonId && send.state === 'too-long')),
    { key, lessonId, href: target.href, state: 'reading', durationSeconds: 0, thumbnailUrl: null, sentBytes: 0, totalBytes: file.size, paused: false },
  ]);
  void run(getToken, key, target, file, stopper.signal);
}

async function run(getToken: GetToken, key: string, target: SendTarget, file: File, signal: AbortSignal) {
  try {
    const probed = await probeVideo(file);
    // Too long is said, never shown as a video: nothing was sent (ruled 16
    // October), so there is no thumbnail and nothing to remove.
    if (probed.durationSeconds > MAX_SECONDS) {
      update(key, { state: 'too-long', durationSeconds: probed.durationSeconds });
      return;
    }
    update(key, { durationSeconds: probed.durationSeconds, thumbnailUrl: URL.createObjectURL(probed.thumbnail) });

    update(key, { state: 'sending' });
    const started = await startUpload(getToken, target, { size_bytes: file.size });
    await sendWhole(started.thumbnail_url, probed.thumbnail, 'image/jpeg', signal);
    await sendInBlocks(started.video_url, file, file.type || 'video/quicktime', signal, (progress) =>
      update(key, { sentBytes: progress.sentBytes, totalBytes: progress.totalBytes, paused: progress.paused }),
    );
    const recorded = await finishUpload(getToken, target, {
      upload_id: started.upload_id,
      duration_seconds: Math.round(probed.durationSeconds * 100) / 100,
    });

    // Kept, full, until the list has the video: dropping it here left a gap
    // and a jump while the list was re-read (16 October). settle() drops it.
    const still = sends.find((send) => send.key === key)?.thumbnailUrl;
    if (still) localThumbnails.set(recorded.id, still);
    update(key, { state: 'done', videoId: recorded.id, sentBytes: file.size, paused: false });
    finished.forEach((reload) => reload());
  } catch (cause) {
    if (signal.aborted) return;
    console.error('A video send failed:', cause);
    update(key, { state: 'failed', paused: false });
  }
}

/** Drop finished sends whose video the lesson's list now shows. */
export function settle(videoIds: string[]): void {
  const shown = new Set(videoIds);
  const left = sends.filter((send) => !(send.state === 'done' && send.videoId && shown.has(send.videoId)));
  if (left.length !== sends.length) publish(left);
}

/** Stop a send, or clear one that failed. */
export function stopSend(key: string): void {
  stoppers.get(key)?.abort();
  drop(key);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useVideoSends(): VideoSend[] {
  return useSyncExternalStore(subscribe, () => sends);
}

/** Called whenever a send finishes, so a lesson page can re-read its videos. */
export function onSendFinished(reload: () => void): () => void {
  finished.add(reload);
  return () => finished.delete(reload);
}
